import React, { useEffect, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';

import FeaturedArticle from '@components/FeedItems/FeaturedArticle';
import Spinner from '@components/Spinner';
import useEventTracking from '@hooks/useEventTracking';
import type { Article } from '@interfaces/homepage';
import { TrackingLocation, impression } from '@tracking';
import { getUserRecommendedArticles } from '@utils/backend-api/articles';
import type { User } from '@utils/backend-api/currentUser';

import ForYouSection from './Section';
import { FOR_YOU_ENGAGEMENT_LABELS, FOR_YOU_SECTION_IDS } from './constants';
import useRegisterSection from './useRegisterSection';

const MAX_FREE_BLURB_LENGTH = 100;
const MAX_FREE_BLURB_WORDS = 10;

const ForYouRecommendedArticles = ({ initialCurrentUser }: { initialCurrentUser?: User }) => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.RecommendedArticles,
  });
  const [articles, setArticles] = useState<Article[]>([]);
  const [popular, setPopular] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const recommendedArticlesDisplayText = useMemo(() => {
    if (!initialCurrentUser) return FOR_YOU_ENGAGEMENT_LABELS.Engaged.NonPro.RecommendedArticles;
    const isPro = initialCurrentUser?.is_pro_subscriber;

    if (popular) {
      return FOR_YOU_ENGAGEMENT_LABELS.NotEngaged.NonPro.RecommendedArticles;
    }

    return isPro
      ? FOR_YOU_ENGAGEMENT_LABELS.Engaged.Pro.RecommendedArticles
      : FOR_YOU_ENGAGEMENT_LABELS.Engaged.NonPro.RecommendedArticles;
  }, [initialCurrentUser, popular]);

  useEffect(() => {
    let cancelled = false;

    const fetchArticles = async () => {
      try {
        const { articles, popular } = await getUserRecommendedArticles();
        if (!cancelled) {
          setArticles(articles);
          setPopular(popular);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchArticles();

    return () => {
      cancelled = true;
    };
  }, []);

  const articlesToDisplay = useMemo(() => {
    return articles.map((article) => ({
      ...article,
      freeBlurb:
        article.freeBlurb.length > MAX_FREE_BLURB_LENGTH
          ? `${article.freeBlurb.split(' ').slice(0, MAX_FREE_BLURB_WORDS).join(' ')}…`
          : article.freeBlurb,
    }));
  }, [articles]);

  useRegisterSection(
    FOR_YOU_SECTION_IDS.RecommendedArticles,
    isLoading || articlesToDisplay.length > 0
  );

  if (!isLoading && articlesToDisplay.length === 0) return null;

  return (
    <ForYouSection
      id={FOR_YOU_SECTION_IDS.RecommendedArticles}
      highlight={recommendedArticlesDisplayText.label}
      subtitle={recommendedArticlesDisplayText.subtitle}
      title={recommendedArticlesDisplayText.title}
    >
      <Track>
        <InView
          onChange={(inView) => {
            inView &&
              trackEvent(
                impression({ impressionElement: FOR_YOU_SECTION_IDS.RecommendedArticles })
              );
          }}
          triggerOnce
        >
          {isLoading ? (
            <Spinner marginY="small" />
          ) : (
            <div className="mt-32 grid grid-cols-1 gap-32 sm:grid-cols-2 md:grid-cols-4">
              {articlesToDisplay.map((article) => (
                <FeaturedArticle
                  key={article.id}
                  className="!border-0 !pb-0 [&_h2]:text-headline-xs [&_p]:text-pretty"
                  featuredArticle={article}
                  isFluid
                />
              ))}
            </div>
          )}
        </InView>
      </Track>
    </ForYouSection>
  );
};

export default ForYouRecommendedArticles;
